import Alpine from 'alpinejs';
import axios from '../../config/axios.js';

const toast = (msg, type = 'info') => window.toast?.(msg, type);

Alpine.data('quizPlay', () => ({
  loading: true,
  busy: false,
  blocked: false,
  blockedMessage: '',
  quizMeta: null,
  session: null,
  currentIndex: 0,
  pending: {},
  flagged: {},
  feedback: '',
  feedbackType: '',

  get questions() {
    return this.session?.questions || [];
  },

  get total() {
    return this.questions.length;
  },

  get answeredCount() {
    return this.questions.filter((q) => q.answered).length;
  },

  get progressPct() {
    if (!this.total) return 0;
    return Math.round((this.answeredCount / this.total) * 100);
  },

  get currentQ() {
    return this.questions[this.currentIndex] || null;
  },

  get selectedOption() {
    const q = this.currentQ;
    if (!q) return null;
    if (q.answered) return q.selected;
    return this.pending[q.id] || null;
  },

  get isFlagged() {
    const q = this.currentQ;
    return q ? !!this.flagged[q.id] : false;
  },

  get canGoPrev() {
    return this.currentIndex > 0 && !this.busy;
  },

  get isLastQuestion() {
    return this.currentIndex >= this.total - 1;
  },

  get nextLabel() {
    if (this.isLastQuestion) {
      const q = this.currentQ;
      if (q && !q.answered && this.pending[q.id]) return 'Selesai';
      if (this.answeredCount === this.total) return 'Lihat Hasil';
      return 'Next Question';
    }
    return 'Next Question';
  },

  async init() {
    const store = Alpine.store('auth');
    if (!store?.isLoggedIn) {
      window.location.href =
        '/login?redirect=' + encodeURIComponent(window.location.pathname);
      return;
    }

    await this.bootstrap();
  },

  async bootstrap() {
    this.loading = true;
    this.blocked = false;
    try {
      const { data: statusRes } = await axios.get('/api/v1/engagement/quiz');
      this.quizMeta = statusRes.data;

      if (this.quizMeta?.today_completed) {
        this.blocked = true;
        this.blockedMessage = 'Anda sudah menyelesaikan quiz hari ini. Kembali lagi besok!';
        return;
      }

      if (!this.quizMeta?.can_play_today) {
        this.blocked = true;
        this.blockedMessage = 'Quiz tidak tersedia saat ini.';
        return;
      }

      const { data: startRes } = await axios.post('/api/v1/engagement/quiz/start');
      if (!startRes.data?.attempt) {
        this.blocked = true;
        this.blockedMessage = startRes.message || 'Tidak bisa memulai quiz.';
        return;
      }

      this.session = startRes.data.attempt;
      this.restoreFlags();
      this.currentIndex = this.firstUnansweredIndex();
      this.clearFeedback();
    } catch (e) {
      this.blocked = true;
      this.blockedMessage =
        e.response?.data?.message || 'Gagal memuat quiz. Coba lagi nanti.';
    } finally {
      this.loading = false;
    }
  },

  flagStorageKey() {
    return `quiz_flags_${this.session?.id || 'none'}`;
  },

  restoreFlags() {
    try {
      const raw = localStorage.getItem(this.flagStorageKey());
      this.flagged = raw ? JSON.parse(raw) : {};
    } catch {
      this.flagged = {};
    }
  },

  persistFlags() {
    try {
      localStorage.setItem(this.flagStorageKey(), JSON.stringify(this.flagged));
    } catch {
      /* ignore */
    }
  },

  firstUnansweredIndex() {
    const idx = this.questions.findIndex((q) => !q.answered);
    return idx >= 0 ? idx : 0;
  },

  cellClass(index) {
    const q = this.questions[index];
    if (!q) return '';
    let cls = 'quiz-play__cell';
    if (index === this.currentIndex) cls += ' is-current';
    if (q.answered) cls += ' is-answered';
    return cls;
  },

  isCellFlagged(index) {
    const q = this.questions[index];
    return q ? !!this.flagged[q.id] : false;
  },

  goTo(index) {
    if (index < 0 || index >= this.total || this.busy) return;
    this.currentIndex = index;
    this.clearFeedback();
  },

  selectOption(key) {
    const q = this.currentQ;
    if (!q || q.answered || this.busy) return;
    this.pending[q.id] = key;
    this.clearFeedback();
  },

  toggleFlag() {
    const q = this.currentQ;
    if (!q) return;
    this.flagged[q.id] = !this.flagged[q.id];
    if (!this.flagged[q.id]) delete this.flagged[q.id];
    this.persistFlags();
  },

  clearFeedback() {
    this.feedback = '';
    this.feedbackType = '';
  },

  saveForLater() {
    window.location.href = '/dashboard/reward';
  },

  async prev() {
    if (!this.canGoPrev) return;
    this.currentIndex -= 1;
    this.clearFeedback();
  },

  async next() {
    if (this.busy || !this.currentQ) return;

    const q = this.currentQ;

    if (!q.answered) {
      const opt = this.pending[q.id];
      if (!opt) {
        toast('Pilih salah satu jawaban terlebih dahulu.', 'error');
        return;
      }
      await this.submitAnswer(q, opt);
      return;
    }

    if (this.isLastQuestion) {
      if (this.session?.status === 'completed') {
        this.finishAndLeave();
      } else if (this.answeredCount < this.total) {
        this.currentIndex = this.firstUnansweredIndex();
      } else {
        this.finishAndLeave();
      }
      return;
    }

    this.currentIndex += 1;
    this.clearFeedback();
  },

  async submitAnswer(q, option) {
    this.busy = true;
    this.clearFeedback();
    try {
      const { data } = await axios.post('/api/v1/engagement/quiz/answer', {
        attempt_id: this.session.id,
        question_id: q.id,
        option,
      });
      const res = data.data || {};

      q.answered = true;
      q.selected = option.toUpperCase();
      delete this.pending[q.id];

      if (res.completed) {
        this.session = res.attempt || this.session;
        this.session.status = 'completed';
        toast(
          `Quiz selesai! ${res.correct}/${res.total} benar · +${res.points || 0} poin`,
          'success',
        );
        setTimeout(() => this.finishAndLeave(), 1200);
        return;
      }

      this.feedback = res.correct
        ? `Benar! +${res.points || 0} poin · +${res.xp || 0} XP`
        : res.explanation || 'Jawaban kurang tepat.';
      this.feedbackType = res.correct ? 'is-correct' : 'is-wrong';

      if (!this.isLastQuestion) {
        setTimeout(() => {
          this.currentIndex += 1;
          this.clearFeedback();
        }, 650);
      }
    } catch (e) {
      toast(e.response?.data?.message || 'Gagal menyimpan jawaban.', 'error');
    } finally {
      this.busy = false;
    }
  },

  finishAndLeave() {
    try {
      localStorage.removeItem(this.flagStorageKey());
    } catch {
      /* ignore */
    }
    window.location.href = '/dashboard/reward';
  },

  optionLabel(key, text) {
    return text || '';
  },
}));
